import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { formatDate } from "@/utils/formatDate";
import {
  useCompanyHolidays,
  useCreateEmployeeDayOff,
  useDeleteEmployeeDayOff,
  useEmployeeDaysOff,
  useOwedDaysOff,
} from "../hooks/useSchedule";
import type { CompanyHoliday, EmployeeDayOff } from "../types";

function rangeLabel(ch: Pick<CompanyHoliday, "start_date" | "end_date">): string {
  return ch.start_date === ch.end_date
    ? formatDate(ch.start_date)
    : `${formatDate(ch.start_date)} – ${formatDate(ch.end_date)}`;
}

/**
 * Same rules as the backend (apps/leaves/days_off.py → already_compensated_error):
 * a holiday is already compensated for this employee when they got an individual day
 * off for it, or when the company day off for it fell on/after their first working day
 * (an unknown first working day = employed all along).
 */
export function alreadyGivenMessage(
  compensatesFor: string,
  entries: EmployeeDayOff[],
  companyHolidays: CompanyHoliday[],
  firstWorkDay: string | null,
): string | null {
  if (!compensatesFor) return null;
  const own = entries.find((e) => e.compensates_for === compensatesFor);
  if (own) {
    return `Dzień wolny za ${formatDate(compensatesFor)} został już oddany temu pracownikowi (${formatDate(own.date)}).`;
  }
  const company = companyHolidays.find((ch) => ch.compensates_for === compensatesFor);
  if (company && (!firstWorkDay || company.end_date >= firstWorkDay)) {
    return `Dzień wolny za ${formatDate(compensatesFor)} został już oddany — firmowy dzień wolny ${rangeLabel(company)}.`;
  }
  return null;
}

interface Props {
  employeeId: string;
  /** CEO / technical director — may grant and remove individual days off. */
  canManage: boolean;
}

/** "Dni wolne za święta" section of the employees panel. */
export function EmployeeDaysOffPanel({ employeeId, canManage }: Props) {
  const { data, isLoading } = useEmployeeDaysOff(employeeId);
  const { data: owed = [] } = useOwedDaysOff(employeeId);
  const { data: companyHolidays = [] } = useCompanyHolidays();
  const createDayOff = useCreateEmployeeDayOff();
  const deleteDayOff = useDeleteEmployeeDayOff();

  const [formOpen, setFormOpen] = useState(false);
  const [day, setDay] = useState("");
  const [compensatesFor, setCompensatesFor] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");

  const entries = data?.entries ?? [];
  const firstWorkDay = data?.firstWorkDay ?? null;
  const alreadyGiven = alreadyGivenMessage(compensatesFor, entries, companyHolidays, firstWorkDay);

  const openForm = (forDate = "") => {
    setDay("");
    setCompensatesFor(forDate);
    setDescription("");
    setError("");
    setFormOpen(true);
  };

  const submit = () => {
    if (!day || !compensatesFor) {
      setError("Podaj dzień wolny i dzień, za który jest oddawany.");
      return;
    }
    createDayOff.mutate(
      { employee: employeeId, date: day, compensates_for: compensatesFor, description: description.trim() || undefined },
      {
        onSuccess: () => setFormOpen(false),
        onError: (err: unknown) => {
          const errs = (err as { response?: { data?: { errors?: Record<string, string[] | string> } } })?.response?.data
            ?.errors;
          const first = (v: string[] | string | undefined) => (Array.isArray(v) ? v[0] : v);
          setError(
            first(errs?.compensates_for) ?? first(errs?.date) ?? first(errs?.employee) ?? "Błąd podczas zapisywania.",
          );
        },
      },
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-gray-700 dark:text-gray-300">Dni wolne za święta</h4>
        {canManage && !formOpen && (
          <Button size="sm" variant="secondary" onClick={() => openForm()}>
            <Plus size={14} className="mr-1.5" /> Wskaż dzień wolny
          </Button>
        )}
      </div>

      {owed.length > 0 && (
        <div className="flex flex-col gap-2 mb-3">
          {owed.map((o) => (
            <div
              key={o.compensates_for}
              className="rounded-md border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 px-3 py-2 flex items-start justify-between gap-3"
            >
              <div className="text-sm text-red-700 dark:text-red-300">
                <p className="font-medium">
                  Temu pracownikowi przysługuje oddanie dnia wolnego za dzień {formatDate(o.compensates_for)}.
                </p>
                <p className="text-xs mt-0.5">
                  Firmowy dzień wolny ({rangeLabel(o.company_holiday)}) przypadł przed jego pierwszym dniem pracy (
                  {formatDate(o.first_work_day)}).
                </p>
              </div>
              {canManage && (
                <Button size="sm" variant="secondary" onClick={() => openForm(o.compensates_for)}>
                  Wskaż dzień
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {canManage && formOpen && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex flex-col gap-3 mb-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Dzień wolny</label>
              <DatePicker
                value={day}
                onChange={(v) => {
                  setDay(v);
                  setError("");
                }}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Oddawany za dzień
              </label>
              <DatePicker
                value={compensatesFor}
                onChange={(v) => {
                  setCompensatesFor(v);
                  setError("");
                }}
              />
            </div>
          </div>
          {alreadyGiven && <p className="text-sm text-red-600">{alreadyGiven}</p>}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Opis <span className="text-gray-400 text-xs">(opcjonalny)</span>
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="np. Za Wniebowzięcie NMP"
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2">
            <Button
              size="sm"
              loading={createDayOff.isPending}
              disabled={createDayOff.isPending || !!alreadyGiven}
              onClick={submit}
            >
              Zapisz
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setFormOpen(false)}>
              Anuluj
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Ładowanie...</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak indywidualnych dni wolnych.</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {entries.map((e) => (
            <div
              key={e.id}
              className="flex items-center justify-between gap-2 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2 bg-gray-50 dark:bg-gray-800/50"
            >
              <div className="min-w-0 text-sm">
                <span className="font-medium text-gray-800 dark:text-gray-200">{formatDate(e.date)}</span>
                <span className="text-xs text-gray-600 dark:text-gray-300 ml-2">za {formatDate(e.compensates_for)}</span>
                {e.description && <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{e.description}</span>}
                {e.created_by_name && (
                  <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">· {e.created_by_name}</span>
                )}
              </div>
              {canManage && (
                <button
                  onClick={() => deleteDayOff.mutate(e.id)}
                  disabled={deleteDayOff.isPending}
                  className="p-1.5 rounded text-gray-400 hover:text-red-500 disabled:opacity-40 transition-colors flex-shrink-0"
                  title="Usuń"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
